import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AxiosError } from 'axios';
import { Aviso, AvisoComDetalhes, AvisoComLeitura } from '../types/avisos';
import { avisoService } from '../services/avisoService';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import PollingService from '../services/PollingService';
import { useNotificacao } from './NotificacaoContext';

interface AvisoContextType {
  avisos: AvisoComLeitura[];
  avisosFixados: AvisoComLeitura[];
  avisosNaoLidos: number;
  carregando: boolean;
  erro: string | null;
  atualizacoesTempoReal: boolean;
  obterAvisos: () => Promise<void>;
  criarAviso: (aviso: Omit<Aviso, 'id' | 'data_criacao'>) => Promise<Aviso>;
  atualizarAviso: (id: string, aviso: Partial<Aviso>) => Promise<any>;
  excluirAviso: (id: string) => Promise<boolean>;
  marcarComoLido: (avisoId: string) => Promise<void>;
  fixarAviso: (avisoId: string) => Promise<void>;
  desfixarAviso: (avisoId: string) => Promise<void>;
  obterAvisoPorId: (id: string) => Promise<AvisoComDetalhes | null>;
  ativarAtualizacoesTempoReal: () => void;
  desativarAtualizacoesTempoReal: () => void;
}

const AvisoContext = createContext<AvisoContextType | undefined>(undefined);

export const useAviso = () => {
  const context = useContext(AvisoContext);
  if (!context) {
    throw new Error('useAviso deve ser usado dentro de um AvisoProvider');
  }
  return context;
};

interface AvisoProviderProps {
  children: ReactNode;
}

export const AvisoProvider: React.FC<AvisoProviderProps> = ({ children }) => {
  const { usuario } = useAuth();
  const { adicionarNotificacao } = useNotificacao();
  const [avisos, setAvisos] = useState<AvisoComLeitura[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizacoesTempoReal, setAtualizacoesTempoReal] = useState<boolean>(true);
  const [primeiroCarregamento, setPrimeiroCarregamento] = useState<boolean>(true);

  // Filtrar avisos fixados
  const avisosFixados = avisos.filter(aviso => aviso.fixado);
  
  // Contar avisos não lidos - usando useMemo para garantir que o valor seja recalculado quando o estado de avisos mudar
  const avisosNaoLidos = React.useMemo(() => {
    return avisos.filter(aviso => !aviso.lido).length;
  }, [avisos]);

  // Carregar avisos ao montar o componente
  // Função para iniciar o polling de avisos
  const iniciarPollingAvisos = () => {
    PollingService.iniciarPollingFuncao(
      'avisos',
      async () => { await obterAvisos(); },
      600000 // 10 minutos
    );
  };

  const pararPollingAvisos = () => {
    PollingService.pararPolling('avisos');
  };
  
  // Funções para ativar/desativar atualizações em tempo real
  const ativarAtualizacoesTempoReal = () => {
    setAtualizacoesTempoReal(true);
    iniciarPollingAvisos();
  };
  
  const desativarAtualizacoesTempoReal = () => {
    setAtualizacoesTempoReal(false);
    pararPollingAvisos();
  };

  useEffect(() => {
    if (usuario) {
      obterAvisos().then(() => {
        // Marcar que o primeiro carregamento foi concluído
        setPrimeiroCarregamento(false);
      });
      
      // Iniciar polling se atualizações em tempo real estiverem ativadas
      if (atualizacoesTempoReal) {
        iniciarPollingAvisos();
      }
    }
    
    return () => {
      pararPollingAvisos();
    };
  }, [usuario, atualizacoesTempoReal]);

  // Função para obter todos os avisos relevantes para o usuário
  const obterAvisos = async () => {
    if (!usuario) return;
    
    setCarregando(true);
    setErro(null);
    
    try {
      const response = await avisoService.obterAvisos();
      
      // Processar avisos para adicionar flag de lido/não lido
      const avisosProcessados = response.avisos.map((aviso: Aviso & { leituras: Array<{usuario_id: string, data_leitura?: string}> }) => {
        const leitura = aviso.leituras.find((l: {usuario_id: string, data_leitura?: string}) => l.usuario_id === usuario.id);
        return {
          ...aviso,
          lido: !!leitura,
          data_leitura: leitura?.data_leitura
        };
      });
      
      // Ordenar por fixados primeiro, depois por data (mais recentes primeiro)
      const avisosOrdenados = avisosProcessados.sort((a: AvisoComLeitura, b: AvisoComLeitura) => {
        if (a.fixado && !b.fixado) return -1;
        if (!a.fixado && b.fixado) return 1;
        return new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime();
      });
      
      setAvisos(avisosOrdenados);
    } catch (error) {
      console.error('Erro ao obter avisos:', error);
      setErro('Não foi possível carregar os avisos. Tente novamente mais tarde.');
      toast.error('Erro ao carregar avisos');
    } finally {
      setCarregando(false);
    }
  };

  // Função para criar um novo aviso
  const criarAviso = async (aviso: Omit<Aviso, 'id' | 'data_criacao'>) => {
    try {
      const novoAviso = await avisoService.criarAviso(aviso);
      
      // Adicionar o novo aviso à lista
      setAvisos(avisosAtuais => [
        {
          ...novoAviso,
          lido: true, // O criador já viu o aviso
          criador: {
            id: usuario!.id,
            nome: usuario!.nome,
            avatar: '/profilepicture.png' // Usando imagem padrão
          },
          leituras: []
        } as AvisoComLeitura,
        ...avisosAtuais
      ]);
      
      toast.success('Aviso criado com sucesso!');
      return novoAviso;
    } catch (error) {
      console.error('Erro ao criar aviso:', error);
      toast.error('Erro ao criar aviso');
      throw error;
    }
  };

  // Função para atualizar um aviso existente
  const atualizarAviso = async (id: string, avisoAtualizado: Partial<Aviso>): Promise<any> => {
    try {
      const resultado = await avisoService.atualizarAviso(id, avisoAtualizado);
      
      // Atualizar o aviso na lista
      setAvisos(avisosAtuais => 
        avisosAtuais.map(aviso => 
          aviso.id === id ? { ...aviso, ...avisoAtualizado } : aviso
        )
      );
      
      toast.success('Aviso atualizado com sucesso!');
      return resultado;
    } catch (error) {
      console.error(`Erro ao atualizar aviso ${id}:`, error);
      toast.error('Erro ao atualizar aviso');
      throw error;
    }
  };

  // Função para excluir um aviso
  const excluirAviso = async (id: string) => {
    try {
      await avisoService.excluirAviso(id);
      
      // Remover o aviso da lista
      setAvisos(avisosAtuais => avisosAtuais.filter(aviso => aviso.id !== id));
      
      toast.success('Aviso excluído com sucesso!');
      return true;
    } catch (error) {
      console.error(`Erro ao excluir aviso ${id}:`, error);
      toast.error('Erro ao excluir aviso');
      throw error;
    }
  };

  // Função para marcar um aviso como lido
  const marcarComoLido = async (avisoId: string) => {
    try {
      // Chamar a API para marcar como lido
      await avisoService.marcarComoLido(avisoId);
      
      // Criar uma cópia do estado atual para manipulação
      const avisosAtualizados = avisos.map(aviso => 
        aviso.id === avisoId 
          ? { ...aviso, lido: true, data_leitura: new Date().toISOString() } 
          : aviso
      );
      
      // Atualizar o estado local imediatamente — não precisa recarregar do servidor
      setAvisos(avisosAtualizados);
    } catch (error) {
      console.error(`Erro ao marcar aviso ${avisoId} como lido:`, error);
      // Não exibimos toast de erro aqui para não interromper a experiência do usuário
    }
  };

  // Função para fixar um aviso
  const fixarAviso = async (avisoId: string) => {
    try {
      await avisoService.alterarFixacao(avisoId, true);
      
      // Atualizar o status de fixação do aviso
      setAvisos(avisosAtuais => 
        avisosAtuais.map(aviso => 
          aviso.id === avisoId ? { ...aviso, fixado: true } : aviso
        )
      );
      
      toast.success('Aviso fixado com sucesso!');
    } catch (error) {
      console.error(`Erro ao fixar aviso ${avisoId}:`, error);
      toast.error('Erro ao fixar aviso');
    }
  };

  // Função para desfixar um aviso
  const desfixarAviso = async (avisoId: string) => {
    try {
      await avisoService.alterarFixacao(avisoId, false);
      
      // Atualizar o status de fixação do aviso
      setAvisos(avisosAtuais => 
        avisosAtuais.map(aviso => 
          aviso.id === avisoId ? { ...aviso, fixado: false } : aviso
        )
      );
      
      toast.success('Aviso desfixado com sucesso!');
    } catch (error) {
      console.error(`Erro ao desfixar aviso ${avisoId}:`, error);
      toast.error('Erro ao desfixar aviso');
    }
  };

  // Função para obter um aviso específico pelo ID
  const obterAvisoPorId = async (id: string) => {
    try {
      return await avisoService.obterAvisoPorId(id);
    } catch (error) {
      console.error(`Erro ao obter aviso ${id}:`, error);
      toast.error('Erro ao carregar detalhes do aviso');
      return null;
    }
  };

  const value = {
    avisos,
    avisosFixados,
    avisosNaoLidos,
    carregando,
    erro,
    atualizacoesTempoReal,
    obterAvisos,
    criarAviso,
    atualizarAviso,
    excluirAviso,
    marcarComoLido,
    fixarAviso,
    desfixarAviso,
    obterAvisoPorId,
    ativarAtualizacoesTempoReal,
    desativarAtualizacoesTempoReal
  };

  return (
    <AvisoContext.Provider value={value}>
      {children}
    </AvisoContext.Provider>
  );
};
